import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { getDatabase, ref, get, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { recordActivity } from '../../../../api/activityApi';
import type { MaintenanceDraftWithDone } from '../types';

/**
 * Custom hook to manage maintenance draft persistence (loading, autosave, etc.)
 * Handles all Firebase interactions for draft data
 */
export const useDraftPersistence = (buildingKey: string | undefined) => {
    const [maintenanceDraft, setMaintenanceDraft] = useState<MaintenanceDraftWithDone>({
        components: {},
        metadata: {
            savedAt: '',
            buildingKey: buildingKey || ''
        }
    });
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    /** The stored draft has been fetched (or found absent); until then the state is a placeholder, not an empty draft */
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [autosaveTimeout, setAutosaveTimeout] = useState<NodeJS.Timeout | null>(null);

    const db = getDatabase();

    // Only the latest load may set the draft: an earlier one finishing late (StrictMode mounts
    // the page twice) would put back the stored draft over what the sync has added since
    const loadRequestRef = useRef(0);

    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds between retries

    /**
     * Load maintenance plan draft from Firebase
     */
    const loadDraft = async (): Promise<void> => {
        if (!buildingKey) return;

        const request = ++loadRequestRef.current;
        setDraftLoaded(false);
        try {
            const draftRef = ref(db, `maintenance/drafts/${buildingKey}`);
            const snapshot = await get(draftRef);
            if (request !== loadRequestRef.current) return;

            if (snapshot.exists()) {
                const draftData = snapshot.val();
                
                const loadedDraft: MaintenanceDraftWithDone = {
                    components: draftData.components || {},
                    metadata: {
                        savedAt: draftData.metadata?.savedAt || formatDateForStorage(new Date()),
                        buildingKey: draftData.metadata?.buildingKey || buildingKey,
                        createdBy: draftData.metadata?.createdBy
                    },
                    // Preserve rejection comment if it exists
                    ...(draftData.rejectionComment && { rejectionComment: draftData.rejectionComment }),
                };
                setMaintenanceDraft(loadedDraft);
            }
            setDraftLoaded(true);
        } catch (error) {
            console.error('Error loading maintenance plan draft:', error);
        }
    };

    /**
     * Autosave function that saves the entire draft to Firebase
     * Includes retry logic - only shows error after all retries fail
     */
    const autosaveDraft = async (draftToSave?: MaintenanceDraftWithDone, retryCount = 0): Promise<void> => {
        if (!buildingKey) return;

        // Use provided draft or current state
        const currentDraft = draftToSave || maintenanceDraft;
        const auth = getAuth();
        const user = auth.currentUser;

        // Update metadata
        const updatedDraft: MaintenanceDraftWithDone = {
            ...currentDraft,
            metadata: {
                ...currentDraft.metadata,
                savedAt: formatDateForStorage(new Date()),
                buildingKey: buildingKey,
                createdBy: user?.uid || currentDraft.metadata?.createdBy || '',
                version: currentDraft.metadata?.version || '1.0',
                priceYear: currentDraft.metadata?.priceYear || new Date().getFullYear()
            }
        };

        try {
            // Save to Firebase (don't update local state to avoid race conditions)
            const draftRef = ref(db, `maintenance/drafts/${buildingKey}`);
            await set(draftRef, updatedDraft);
            recordActivity('maintenancePlan.updated', { buildingKey, targetKey: buildingKey });

            // Clear any previous error on successful save
            if (saveStatus === 'error') {
                setSaveStatus('idle');
            }
        } catch (error) {
            console.error(`Error autosaving draft (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error);

            if (retryCount < MAX_RETRIES - 1) {
                // Retry after delay
                setTimeout(() => {
                    autosaveDraft(draftToSave, retryCount + 1);
                }, RETRY_DELAY);
            } else {
                // All retries exhausted - show error
                console.error('Autosave failed after all retries');
                setSaveStatus('error');
            }
        }
    };

    /**
     * Schedule autosave with debouncing
     */
    const scheduleAutosave = (draftToSave?: MaintenanceDraftWithDone): void => {
        if (autosaveTimeout) {
            clearTimeout(autosaveTimeout);
        }
        
        const timeout = setTimeout(() => {
            autosaveDraft(draftToSave);
        }, 1000); // 1 second delay
        
        setAutosaveTimeout(timeout);
    };

    // Cleanup autosave timeout on unmount
    useEffect(() => {
        return () => {
            if (autosaveTimeout) {
                clearTimeout(autosaveTimeout);
            }
        };
    }, [autosaveTimeout]);

    return {
        maintenanceDraft,
        setMaintenanceDraft,
        saveStatus,
        draftLoaded,
        loadDraft,
        scheduleAutosave
    };
};
